import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { faArrowRightLong } from "@fortawesome/pro-light-svg-icons";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { StatusCode } from "api/protocols";
import { UserLogout } from "api/rpc";
import { userLogout } from "redux/actions/auth";
import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import { useGuestPortalContext } from "../GuestPortalContext";
import { ButtonNew as Button } from "components/buttonNew/index";
import Callout from "components/callout/Callout";
import Spin from "components/spin/spin";

import "./portalNavigation.scss";
import "../../Customer/ClientPortal/Home/clientPortalHome.scss";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import classNames from "classnames";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import { TFunction } from "i18next";
import StatusBannerStack from "components/statusBanner/StatusBannerStack";

type NavLinkType = Partial<FontAwesomeIconProps> & {
  title: string;
  url: string;
  icon: IconProp; //define icon as being necessary
};

interface IState {
  profileDetailsOpen: boolean;
}

/** Structures the Layout for the Guest Portal based on acitve user.
 * -  Wrap Portal page with in component to provide layout.  Wrapped component will display as the main content.
 * -  Requires to be a child of a GuestPortalProvider
 */
function PortalNavigation(props: { children: any }) {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { updatePortalState, portalState } = useGuestPortalContext();
  const [mobileLeftMenuVisible, setMobileLeftMenuVisible] = useState<boolean>(false);
  const windowSize = useWindowSize();
  const profileContainer = useRef<HTMLDivElement>();
  const [state, setState] = useState<IState>({
    profileDetailsOpen: false,
  });

  /* TODO: Build out subOptions UI if needed */
  const portalNavigationLinks: Array<NavLinkType & { subOptions?: NavLinkType[] }> = [
    {
      title: t("Overview"),
      url: `/tee-on/portal/overview`,
      icon: ["far", "user"] as IconProp,
    },
    {
      title: t("Bookings"),
      url: `/tee-on/portal/bookings`,
      icon: ["far", "golf-flag-hole"] as IconProp,
    },
    {
      title: t("Reservations"),
      url: `/tee-on/portal/reservations`,
      icon: ["far", "calendar"] as IconProp,
    },

    ...(portalState.showLoyaltyTab ? [{
      title: t("Loyalty Program"),
      url: `/tee-on/portal/loyalty-program`,
      icon: ["far", "gift"] as IconProp,
    }] : []),


    {
      title: t("Payment Methods"),
      url: `/tee-on/portal/payment-methods`,
      icon: ["far", "credit-card"] as IconProp,
    },
    {
      title: t("Billing"),
      url: `/tee-on/portal/billing`,
      icon: ["far", "money-check-dollar-pen"] as IconProp,
    },
    {
      title: t("Memberships"),
      url: `/tee-on/portal/memberships`,
      icon: ["far", "id-card-clip"] as IconProp,
    },
    {
      title: t("Tickets"),
      url: `/tee-on/portal/tickets`,
      icon: ["far", "ticket"] as IconProp,
    },
    {
      title: t("Playing Partners"),
      url: `/tee-on/portal/playing-partners`,
      icon: ["far", "users"] as IconProp,
    },
    {
      title: t("Favourites"),
      url: `/tee-on/portal/favourites`,
      icon: ["far", "heart"] as IconProp,
    },
    {
      title: t("Preferences"),
      url: `/tee-on/portal/preferences`,
      icon: ["far", "sliders-up"] as IconProp,
    },
  ];

  // Redirect user if not logged in
  useEffect(() => {
    if (portalState.guest === null) {
      sendUserToLogin();
    }
  }, [portalState.guest]);

  async function logoutUser() {
    const logoutRes = await UserLogout(true);

    if (logoutRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error Logging Out")));
      return;
    }
    dispatch(userLogout());
    updatePortalState({ guest: undefined });
    sendUserToLogin();
  }

  function sendUserToLogin() {
    history.replace(`/tee-on/portal/login`);
  }

  useOutsideAlerter(profileContainer, () => {
    setState(prevState => ({ ...prevState, profileDetailsOpen: false }));
  });

  return (
    <>
      <div className="portal-content-container">
        {mobileLeftMenuVisible && (
          <div className="ui-layout_left-nav-mobile">
            <div className="ui-left-nav_container">
              <div className="ui-left-nav_content">
                <div className="ui-left-nav-logo" style={{ marginBottom: "10px" }}>
                  <img src="../../../public/images/tee_on_menu_logo.png" alt={t('Tee-On Logo')} />
                </div>
                <div className="ui-left-nav-header">
                  <div style={{ fontSize: "14px" }}>{t('Profile Settings')}</div>
                </div>
                <div className="ui-left-nav-menu">
                  <div className="portal-sidebar">
                    <ul id="portal-navigation-list">
                      {portalNavigationLinks.map(({ title, url, ...rest }) => {
                        return (
                          <li key={title + "_" + url}>
                            <NavLink
                              to={url}
                              activeClassName="active"
                              onClick={
                                mobileLeftMenuVisible && windowSize.width <= MOBILE_WIDTH
                                  ? () => setMobileLeftMenuVisible(false)
                                  : null
                              }
                            >
                              &nbsp;
                              <FontAwesomeIcon {...(rest as FontAwesomeIconProps)} />
                              &nbsp; &nbsp;
                              {title}
                            </NavLink>
                          </li>
                        );
                      })}

                      <li>
                        <NavLink onClick={() => logoutUser()} to={"/tee-on/portal/login"} activeClassName="none">
                          &nbsp;
                          <FontAwesomeIcon icon={["far", "sign-out"]} />
                          &nbsp; &nbsp;
                          {t("Logout")}
                        </NavLink>
                      </li>
                    </ul>
                    <div className="portal-sidebar-bottom-bar">
                      {/* <Button
                        style={{ marginRight: "1rem" }}
                        size="medium"
                        type="primary"
                        block
                        onClick={() => navigateToPage("/tee-on/courses", true)}
                      >
                        Book a Tee Time
                        <FontAwesomeIcon style={{ marginLeft: "5px" }} icon={["far", "golf-flag-hole"]} />
                      </Button> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {mobileLeftMenuVisible && (
          <>
            <div
              onClick={() => setMobileLeftMenuVisible(prev => !prev)}
              className="ui-layout_left-nav-mobile-backdrop"
            />
            <FontAwesomeIcon
              onClick={() => setMobileLeftMenuVisible(prev => !prev)}
              className="ui-layout_left-nav-mobile-backdrop-close-button"
              icon={faXmark}
            />
          </>
        )}

        {windowSize.width > MOBILE_WIDTH && (
          <div className="left-menu-container-desktop">
            <div className="logo-container">
              <img className="reservations-navigation-tee-on-logo" src="../../../../public/images/Tee-On--KO.png"></img>
            </div>
            <div className="profile-settings-heading">
              <p>{t('Profile Settings')}</p>
            </div>
            <div className="portal-sidebar">
              <ul id="portal-navigation-list">
                {portalNavigationLinks.map(({ title, url, ...rest }) => {
                  return (
                    <li key={title + "_" + url}>
                      <NavLink to={url} activeClassName="active">
                        &nbsp;
                        <FontAwesomeIcon {...(rest as FontAwesomeIconProps)} />
                        &nbsp; &nbsp;
                        {title}
                      </NavLink>
                    </li>
                  );
                })}
                <li>
                  <NavLink onClick={logoutUser} to={"/tee-on/portal/login"} activeClassName="none">
                    &nbsp;
                    <FontAwesomeIcon icon={["far", "sign-out"]} />
                    &nbsp; &nbsp;
                    {t("Logout")}
                  </NavLink>
                </li>
              </ul>
              <div className="portal-sidebar-bottom-bar">
                {/* <Button
                  style={{ marginRight: "1rem" }}
                  size="medium"
                  type="primary"
                  block
                  onClick={() => navigateToPage("/tee-on/courses", true)}
                >
                  Book a Tee Time
                  <FontAwesomeIcon style={{ marginLeft: "5px" }} icon={["far", "golf-flag-hole"]} />
                </Button> */}
              </div>
            </div>
          </div>
        )}
        {/* <div className="left-menu-container-desktop">
          <div className="logo-container">
            <img className="reservations-navigation-tee-on-logo" src="../../../../public/images/Tee-On--KO.png"></img>
          </div>
          <div className="profile-settings-heading">
            <p>Profile Settings</p>
          </div>
          <div className="portal-sidebar">
            <ul id="portal-navigation-list">
              {portalNavigationLinks.map(({ title, url, subOptions, ...rest }) => {
                return (
                  <li key={title + "_" + url}>
                    <NavLink to={url} activeClassName="active">
                      &nbsp;
                      <FontAwesomeIcon {...(rest as FontAwesomeIconProps)} />
                      &nbsp; &nbsp;
                      {title}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </div> */}

        <div
          className="ui-main-container"
          style={mobileLeftMenuVisible && windowSize.width <= MOBILE_WIDTH ? { filter: "blur(5px)" } : {}}
        >
          <StatusBannerStack surface="tee_on_portal" />
          <div className="portal-navigation-header">
            <div
              className={classNames("portal-navigation-bar", {
                "portal-navigation-bar-mobile": windowSize.width <= MOBILE_WIDTH,
              })}
            >
              {windowSize.width <= MOBILE_WIDTH && (
                <img className="reservations-navigation-tee-on-logo" src="/public/images/Tee-On.png" />
              )}
              <div>
                {windowSize.width > MOBILE_WIDTH ? (
                  <div>
                    {portalState.guest ? (
                      <div
                        className="portal-navigation-dropdown"
                        ref={profileContainer}
                        onClick={() => setState(prevState => ({ ...prevState, profileDetailsOpen: true }))}
                      >
                        <p className="portal-navigation-user-info-name">
                          Hi, {portalState?.guest?.first_name}{" "}
                          {portalState?.guest?.last_name
                            ? `${portalState?.guest?.last_name?.charAt(0)?.toUpperCase()}.`
                            : ""}
                        </p>
                        <div className="portal-navigation-profile-image-container">
                          {portalState?.guest?.profile_image_source ? (
                            <img
                              className="portal-navigation-profile-image"
                              src={portalState?.guest?.profile_image_source}
                              alt={t('User profile image')}
                            />
                          ) : (
                            <div className="client-portal-profile-default-image">
                              <p>
                                {portalState?.guest?.first_name?.charAt(0)?.toUpperCase()}
                                {portalState?.guest?.last_name?.charAt(0)?.toUpperCase()}
                              </p>
                            </div>
                          )}
                        </div>
                        <div
                          className={classNames("portal-navigation-dropdown-container", {
                            "portal-navigation-display-user-info-dropdown": state.profileDetailsOpen,
                          })}
                        >
                          <FontAwesomeIcon
                            onClick={e => {
                              e.stopPropagation();
                              setState(prevState => ({ ...prevState, profileDetailsOpen: false }));
                            }}
                            icon={faXmark}
                            className="portal-navigation-close-button"
                          />
                          <div className="portal-navigation-user-info-container">
                            <div className="portal-navigation-profile-image-container">
                              {portalState?.guest?.profile_image_source ? (
                                <img
                                  className="portal-navigation-profile-image"
                                  src={portalState?.guest?.profile_image_source}
                                  alt={t('User profile image')}
                                />
                              ) : (
                                <div className="client-portal-profile-default-image">
                                  <p>
                                    {portalState?.guest?.first_name?.charAt(0)?.toUpperCase()}
                                    {portalState?.guest?.last_name?.charAt(0)?.toUpperCase()}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="portal-navigation-user-name">Hello, {`${portalState?.guest?.full_name}`}</p>
                              <p className="portal-navigation-user-email">{portalState?.guest?.email}</p>
                            </div>
                          </div>
                          <div
                            className="portal-navigation-actions-container"
                            onClick={e => {
                              e.stopPropagation();
                              setState(prevState => ({ ...prevState, profileDetailsOpen: false }));
                            }}
                          >
                            <div onClick={logoutUser} className="portal-navigation-action">
                              <FontAwesomeIcon
                                className="portal-navigation-action-icon"
                                icon={["far", "right-from-bracket"]}
                              />
                              <span>{t('Logout')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Button
                          onClick={sendUserToLogin}
                          disabled={portalState.loading}
                          className="portal-navigation-login-button"
                        >
                          <span className="portal-navigation-login-button-label">{t('Login')}</span>
                          <FontAwesomeIcon icon={faArrowRightLong} />
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="header-menu-icon" onClick={() => setMobileLeftMenuVisible(prev => !prev)}>
                    <div className="portal-navigation-dropdown">
                      <p className="portal-navigation-user-info-name">
                        Hi, {portalState?.guest?.first_name}{" "}
                        {portalState?.guest?.last_name
                          ? `${portalState?.guest?.last_name?.charAt(0)?.toUpperCase()}.`
                          : ""}
                      </p>
                      <div className="portal-navigation-profile-image-container">
                        {portalState?.guest?.profile_image_source ? (
                          <img
                            className="portal-navigation-profile-image"
                            src={portalState?.guest?.profile_image_source}
                            alt={t('User profile image')}
                          />
                        ) : (
                          <div className="client-portal-profile-default-image">
                            <p>
                              {portalState?.guest?.first_name?.charAt(0)?.toUpperCase()}
                              {portalState?.guest?.last_name?.charAt(0)?.toUpperCase()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="portal-content">
            {renderContent(!!portalState.guest, portalState.loading, props.children, t)}
          </div>
        </div>
      </div>
    </>
  );
}

/** Renders either a Spinner || Callout || Children */
const renderContent = (
  activeUser: boolean,
  userLoading: boolean,
  children: any,
  t: TFunction<"translation", undefined>,
) => {
  // Trying to load user
  if (userLoading) {
    return (
      <div className="portal-spinner">
        <span>
          <Spin />
        </span>
      </div>
    );
  } else {
    // check if user found
    if (!activeUser) {
      return (
        <div className="portal-callout">
          <Callout
            type={"error"}
            title={t("No user is logged in.")}
            content={t("Please log into your account to see detailed information about your profile.")}
          />
        </div>
      );
    }
  }

  // Not loading && active user
  return children as JSX.Element;
};

export default PortalNavigation;
